import type { QueryClient } from '@tanstack/react-query';
import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import {
  getSessionState,
  loginBySessionConfig,
  transitionSession,
} from '@/entities/user/session';
import { LoginStatus } from '@/entities/user/user';
import { prepareApplication } from '@/features/reference-data-prefetch';
import { getErrorStatus } from '@/shared/api/intercepted-error';
import {
  CHANGE_PASSWORD,
  HOME,
  LOGIN,
  MERGE_ACCOUNTS,
} from '@/shared/config/routes';

export const createPrefetchLoader =
  (queryClient: QueryClient) =>
  async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    if ([LOGIN, CHANGE_PASSWORD, MERGE_ACCOUNTS].includes(url.pathname))
      return null;

    const status = getSessionState(queryClient).loginStatus;
    // Route changes within an established session must not restore an expired profile.
    if (![LoginStatus.pending, LoginStatus.unauthorized].includes(status))
      return null;

    const loginRedirect = () => {
      const from = url.pathname === HOME ? '' : `${url.pathname}${url.search}`;
      return redirect(
        from ? `${LOGIN}?next=${encodeURIComponent(from)}` : LOGIN,
      );
    };

    try {
      await queryClient.fetchQuery({
        ...loginBySessionConfig,
        queryKey: loginBySessionConfig.queryKey!,
        staleTime: 0,
      });
    } catch (error) {
      if (![401, 403].includes(getErrorStatus(error) ?? 0)) throw error;
      if (getSessionState(queryClient).loginStatus === LoginStatus.loggingOut)
        return null;
      transitionSession(queryClient, LoginStatus.unauthorized);
      return loginRedirect();
    }

    if (getSessionState(queryClient).loginStatus === LoginStatus.loggingOut)
      return null;
    if (
      [LoginStatus.pending, LoginStatus.unauthorized].includes(
        getSessionState(queryClient).loginStatus,
      )
    ) {
      transitionSession(queryClient, LoginStatus.preparing);
    }
    await prepareApplication(queryClient);
    return getSessionState(queryClient).loginStatus === LoginStatus.unauthorized
      ? loginRedirect()
      : null;
  };
